import { DeleteDocumentButton } from './DeleteDocumentButton';
import { EncounterReportList } from './EncounterReportList';
import { TZDate } from '@date-fns/tz';
import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Calendar, Plus, Upload } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { TimelineItem } from './TimelineItem';
import { Button } from '@/components/ui/button';
import { useOrgTimezone } from '@/lib/timezone';
import { Card, CardContent } from '@/components/ui/card';
import { hasLabTag, hasRxTag } from '../utils/tagMatchers';
import { P, RequirePermission } from '@/features/permissions';
import { useUploadedDocuments } from '../api/uploaded-documents-api';
import { type EncounterOut, useEncounterTimeline } from '../api/encounters-api';
import { type GeneratedDocumentOut, useDocuments } from '../api/documents-api';
import type { TimelineDate } from '@/components/encounter/InlineDocumentViewer';
import type { TimelineFilterKind, TimelineItem as TimelineItemType, UploadedDocumentOut } from '../types';

/**
 * Comparable epoch value for a timeline date.
 *
 * A calendar date is anchored to NOON in the active zone. Anchoring inside the
 * zone is what keeps it on its own day; noon rather than midnight puts it in
 * the middle of that day, so it sorts among the instants recorded the same day
 * instead of pinned to one edge. Relative order of two calendar dates is
 * unaffected by the choice.
 */
export function toSortKey(date: TimelineDate, timeZone: string): number {
  if (date.kind === 'instant') return new Date(date.value).getTime();

  const [year, month, day] = date.value.split('-').map(Number);
  return new TZDate(year, month - 1, day, 12, 0, 0, timeZone).getTime();
}

// Plan 08: an uploaded doc counts as a Lab/Prescription if its
// `document_type` matches OR any of its tags is a canonical match.
const isLabUploaded = (u: UploadedDocumentOut): boolean => u.document_type === 'lab_result' || hasLabTag(u.tags);

const isRxUploaded = (u: UploadedDocumentOut): boolean => u.document_type === 'prescription' || hasRxTag(u.tags);

/**
 * The visit a generated report documents: the latest encounter_date among its
 * source encounters that are loaded. A report is dated by its visit, so a
 * 4 August recording signed in October sits beside its 4 August encounter
 * instead of reading as a second, today-dated visit. Undefined when no source
 * encounter is loaded; the caller then falls back to generated_at.
 */
export function reportVisitDate(
  sourceEncounterIds: string[] | undefined,
  encounterDates: ReadonlyMap<string, string>,
): string | undefined {
  const dates = (sourceEncounterIds ?? []).map((id) => encounterDates.get(id)).filter((d): d is string => !!d);
  return dates.length ? dates.sort().at(-1) : undefined;
}

interface UnifiedTimelineProps {
  allowEncounterDeletion?: boolean;
  onOpenEncounter?: (encounter: EncounterOut) => void; // NEW — when omitted, encounters navigate
  onOpenGeneratedDocument: (doc: GeneratedDocumentOut) => void;
  onOpenUploadedDocument: (doc: UploadedDocumentOut) => void;
  onUploadClick: () => void;
  patientId: string;
  showUploadButton?: boolean;
}

const FILTER_OPTIONS: { value: TimelineFilterKind; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'encounter', label: 'Encounters' },
  { value: 'generated', label: 'Generated' },
  { value: 'uploaded', label: 'Uploaded' },
  { value: 'lab', label: 'Labs' },
  { value: 'prescription', label: 'Prescriptions' },
];

export function UnifiedTimeline({
  allowEncounterDeletion = false,
  patientId,
  onUploadClick,
  onOpenEncounter,
  onOpenUploadedDocument,
  onOpenGeneratedDocument,
  showUploadButton = true,
}: UnifiedTimelineProps) {
  const navigate = useNavigate();
  const [encounterType, setEncounterType] = useState('all');
  const [filter, setFilter] = useState<TimelineFilterKind>('all');
  const { timeZone } = useOrgTimezone();

  const encounterQuery = useEncounterTimeline(patientId);
  const { data: documentsData } = useDocuments(patientId ? { limit: 100, patient_id: patientId } : undefined);
  const { data: uploadedData } = useUploadedDocuments(patientId ? { limit: 100, patient_id: patientId } : undefined);

  const encounters = useMemo(
    () => encounterQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [encounterQuery.data],
  );
  const documents = documentsData?.data ?? [];
  const uploaded = uploadedData?.data ?? [];

  const labCount = useMemo(() => uploaded.filter(isLabUploaded).length, [uploaded]);
  const prescriptionCount = useMemo(() => uploaded.filter(isRxUploaded).length, [uploaded]);

  const timelineItems = useMemo(() => {
    const items: TimelineItemType[] = [];

    if (filter === 'all' || filter === 'encounter' || filter === 'generated') {
      // encounter_date is a calendar date.
      encounters
        .filter((e) => filter !== 'generated' || documents.some((d) => d.source_encounter_ids?.includes(e.id)))
        .forEach((e) =>
          items.push({ data: e, kind: 'encounter', date: { kind: 'calendar', value: e.encounter_date } }),
        );
    }

    if (filter === 'all' || filter === 'generated') {
      // Dated by the visit it documents (a calendar date); generated_at (an
      // instant) only when its source encounter is not loaded.
      const encounterDates = new Map(encounters.map((e) => [e.id, e.encounter_date]));
      documents.forEach((d) => {
        // Reports with a loaded source visit appear only inside that encounter.
        if (d.source_encounter_ids?.some((id) => encounterDates.has(id))) return;
        const visit = reportVisitDate(d.source_encounter_ids, encounterDates);
        items.push({
          data: d,
          kind: 'generated',
          date: visit ? { kind: 'calendar', value: visit } : { kind: 'instant', value: d.generated_at },
        });
      });
    }

    if (filter === 'all' || filter === 'uploaded') {
      // document_date is a calendar date.
      uploaded.forEach((u) =>
        items.push({ data: u, kind: 'uploaded', date: { kind: 'calendar', value: u.document_date } }),
      );
    }

    if (filter === 'lab') {
      uploaded
        .filter(isLabUploaded)
        .forEach((u) => items.push({ data: u, kind: 'lab', date: { kind: 'calendar', value: u.document_date } }));
    }

    if (filter === 'prescription') {
      uploaded
        .filter(isRxUploaded)
        .forEach((u) =>
          items.push({ data: u, kind: 'prescription', date: { kind: 'calendar', value: u.document_date } }),
        );
    }

    items.sort((a, b) => toSortKey(b.date, timeZone) - toSortKey(a.date, timeZone));
    return encounterType === 'all'
      ? items
      : items.filter((item) => item.kind === 'encounter' && item.data.encounter_type === encounterType);
  }, [encounters, documents, uploaded, filter, timeZone, encounterType]);

  const handleItemClick = (item: TimelineItemType) => {
    switch (item.kind) {
      case 'encounter':
        if (onOpenEncounter) {
          onOpenEncounter(item.data);
        } else {
          navigate({
            to: '/encounters/$encounterId',
            params: { encounterId: item.data.id },
          });
        }
        break;
      case 'generated':
        onOpenGeneratedDocument(item.data);
        break;
      case 'lab':
      case 'prescription':
      case 'uploaded':
        onOpenUploadedDocument(item.data);
        break;
    }
  };

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-1">
          {FILTER_OPTIONS.map((option) => (
            <Button
              key={option.value}
              size="sm"
              className="h-8 text-xs"
              variant={filter === option.value ? 'default' : 'outline'}
              onClick={() => {
                setFilter(option.value);
                setEncounterType('all');
              }}>
              {option.label}
              {option.value !== 'all' && (
                <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                  {option.value === 'encounter'
                    ? encounters.length
                    : option.value === 'generated'
                      ? documents.length
                      : option.value === 'lab'
                        ? labCount
                        : option.value === 'prescription'
                          ? prescriptionCount
                          : uploaded.length}
                </Badge>
              )}
            </Button>
          ))}
        </div>

        {showUploadButton && (
          <RequirePermission permission={P.UPDATE_DOCUMENT}>
            <Button size="sm" variant="outline" onClick={onUploadClick}>
              <Upload className="mr-2 h-4 w-4" />
              Upload Document
            </Button>
          </RequirePermission>
        )}
      </div>

      {encounters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm">
            Encounter type
            <select
              className="min-h-11 rounded-md border bg-background px-2"
              value={encounterType}
              onChange={(event) => {
                const type = event.target.value;
                setEncounterType(type);
                setFilter(type === 'all' ? 'all' : 'encounter');
              }}>
              <option value="all">All types</option>
              {[...new Set(encounters.map((e) => e.encounter_type).filter((v): v is string => Boolean(v)))]
                .sort()
                .map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
            </select>
          </label>
        </div>
      )}
      {encounterQuery.hasNextPage && (
        <Button
          variant="outline"
          disabled={encounterQuery.isFetchingNextPage}
          onClick={() => void encounterQuery.fetchNextPage()}>
          {encounterQuery.isFetchingNextPage ? 'Loading older encounters…' : 'Load older encounters'}
        </Button>
      )}
      {encounterQuery.isError && (
        <p role="alert">
          Encounter history could not be fully loaded.{' '}
          <Button variant="outline" onClick={() => void encounterQuery.refetch()}>
            Retry
          </Button>
        </p>
      )}
      {/* Timeline items */}
      {timelineItems.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Calendar className="h-10 w-10 mb-2" />
            <p>No items to display.</p>
            <RequirePermission permission={P.CREATE_ENCOUNTER}>
              <Button
                size="sm"
                className="mt-3"
                variant="outline"
                onClick={() => navigate({ to: '/encounters/new', search: { patientId } })}>
                <Plus className="mr-2 h-4 w-4" />
                Create First Encounter
              </Button>
            </RequirePermission>
          </CardContent>
        </Card>
      ) : (
        timelineItems.map((item, index) => (
          <TimelineItem
            summaryContent={item.kind === 'encounter' ? <EncounterReportList encounterId={item.data.id} /> : undefined}
            actions={
              allowEncounterDeletion && item.kind === 'generated' ? (
                <DeleteDocumentButton document={item.data} />
              ) : undefined
            }
            key={`${item.kind}-${item.data.id}`}
            item={item}
            isLast={index === timelineItems.length - 1}
            onClick={handleItemClick}
          />
        ))
      )}
    </div>
  );
}
